/**
 * Base class for analytics providers
 * Defines the interface that all providers must implement
 */
export class BaseProvider {
  constructor(config = {}) {
    this.config = config;
    this.eventQueue = [];
    this.isProcessing = false;
    this.retryCount = 0;
    this.maxRetries = config.maxRetries || 3;
    this.batchSize = config.batchSize || 10;
    this.batchTimeout = config.batchTimeout || 10000;
    this.debug = config.debug || false;
    this.offlineStorage = config.offlineStorage || true;
    this.initialized = false;
  }

  /**
   * Initialize the provider
   * @returns {Promise<void>}
   */
  async initialize() {
    throw new Error('Provider must implement initialize()');
  }

  /**
   * Track a single event
   * @param {string} eventName - Name of the event
   * @param {Object} eventData - Event data
   * @returns {Promise<void>}
   */
  async track(eventName, eventData = {}) {
    throw new Error('Provider must implement track()');
  }

  /**
   * Track multiple events in batch
   * @param {Array<{name: string, data: Object}>} events - Array of events
   * @returns {Promise<void>}
   */
  async trackBatch(events) {
    throw new Error('Provider must implement trackBatch()');
  }

  /**
   * Queue an event for batch processing
   * @param {string} eventName - Name of the event
   * @param {Object} eventData - Event data
   */
  queueEvent(eventName, eventData = {}) {
    this.eventQueue.push({
      name: eventName,
      data: eventData,
      timestamp: new Date().toISOString()
    });

    if (this.eventQueue.length >= this.batchSize) {
      this.processQueue();
    } else if (!this.isProcessing) {
      setTimeout(() => this.processQueue(), this.batchTimeout);
    }
  }

  /**
   * Process the event queue
   * @returns {Promise<void>}
   */
  async processQueue() {
    if (this.isProcessing || this.eventQueue.length === 0) return;

    this.isProcessing = true;
    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await this.trackBatch(events);
      this.retryCount = 0;
    } catch (error) {
      console.error('Failed to process analytics queue:', error);
      
      // Put events back in queue if we haven't exceeded max retries
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.eventQueue = [...events, ...this.eventQueue];
        setTimeout(() => this.processQueue(), 1000 * this.retryCount);
      } else if (this.offlineStorage) {
        // Store failed events in localStorage
        this.storeOfflineEvents(events);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Store events in offline storage
   * @param {Array} events - Events to store
   */
  storeOfflineEvents(events) {
    try {
      const storedEvents = JSON.parse(localStorage.getItem('analytics_offline_events') || '[]');
      localStorage.setItem('analytics_offline_events', JSON.stringify([...storedEvents, ...events]));
    } catch (error) {
      console.error('Failed to store offline events:', error);
    }
  }

  /**
   * Process any stored offline events
   * @returns {Promise<void>}
   */
  async processOfflineEvents() {
    try {
      const storedEvents = JSON.parse(localStorage.getItem('analytics_offline_events') || '[]');
      if (storedEvents.length > 0) {
        await this.trackBatch(storedEvents);
        localStorage.removeItem('analytics_offline_events');
      }
    } catch (error) {
      console.error('Failed to process offline events:', error);
    }
  }

  /**
   * Flush any pending events
   * @param {boolean} force - Whether to force immediate processing
   * @returns {Promise<void>}
   */
  async flush(force = false) {
    if (force) {
      await this.processQueue();
    } else {
      this.processQueue();
    }
  }
} 